// src/components/desk/ChatInbox.jsx — the agents' side of messaging (owner 2026-09-23, core 2240 + 2280).
//
// "User registers and texts and agent receives it", "like social platforms", and above all: "when agent receives a
// message they must SEE notifications". So the header button does three things, whatever tab the agent is on:
//   1. every 10 s it asks the server what arrived for them (/chat/notify) — a message from a website visitor or a
//      colleague → a pop-up in the corner (click = open that chat), a short sound, and a system notification when
//      the Desk is in the background (permission is asked on the first click of the button);
//   2. the red badge counts everything unread;
//   3. the panel: "Clients" (website visitors) and "Team" (direct chats, a flat's thread, a client's thread), photos,
//      typing, "seen". The server decides who reads and writes; the browser only asks.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  addChatMembers, createGroupChat, getChatFlagged, getChatInbox, getChatNotify, getChatThread, getChatUnread, markChatSeen, muteChat,
  openClientChat, openDirectChat, openPropertyChat, postChatMessage, sendChatTyping, uploadChatPhoto,
} from '../../db/core.js'
import { t } from '../../lib/i18n.js'
import { Icon } from '../Icon.jsx'

const SITE = 'https://sentavus.com'

// "Discuss with the team" (owner 2026-09-23, messaging phase 2): opens — or creates — the team thread about ONE flat
// (the server keys it on the cluster, so every row of the flat lands in the same thread) or ONE client, and hands it
// to the header's chat panel through the `sentavus:open-chat` event. A client thread is its agents' and management's
// only; anyone else is told so here rather than getting a silent failure.
export function DiscussWithTeamButton({ propertyId = null, requirementId = null, className = '', compact = false }) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  if (!propertyId && !requirementId) return null
  const open = async () => {
    if (busy) return
    setBusy(true); setErr('')
    try {
      const r = propertyId ? await openPropertyChat(propertyId) : await openClientChat(requirementId)
      if (r?.id) window.dispatchEvent(new CustomEvent('sentavus:open-chat', { detail: { id: r.id } }))
    } catch (e) {
      setErr(e?.status === 403 ? t('chat_discuss_not_yours') : t('chat_send_failed'))
    } finally { setBusy(false) }
  }
  return (
    <span className="inline-flex flex-col">
      <button type="button" onClick={open} disabled={busy} title={t('chat_discuss_team')} data-testid="chat-discuss-team"
        className={className || 'inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground hover:bg-accent disabled:opacity-50'}>
        <Icon name="message-circle" size={13} />{!compact && <span>{t('chat_discuss_team')}</span>}
      </button>
      {err && <span className="mt-0.5 text-[11px] text-destructive">{err}</span>}
    </span>
  )
}
const NOTIFY_MS = 10000

function initials(name) {
  return String(name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map(s => s.charAt(0).toUpperCase()).join('')
}
function when(iso) {
  const d = new Date(iso), now = new Date()
  return d.toDateString() === now.toDateString()
    ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString([], { day: 'numeric', month: 'short' })
}
// the name a conversation goes by in a list or a header
function titleOf(c) {
  if (!c) return ''
  if (c.kind === 'visitor_agent') return c.visitor?.name || c.visitor?.email || t('chat_visitor')
  if (c.kind === 'direct') return c.with?.name || t('chat_colleague')
  if (c.kind === 'property') {
    const s = c.subject || {}
    return [t('chat_about_property'), s.rooms ? `${s.rooms} ${t('chat_rooms')}` : null, s.address].filter(Boolean).join(' · ')
  }
  if (c.kind === 'client') return `${t('chat_about_client')}${c.subject?.title ? ` · ${c.subject.title}` : ''}`
  // groups + the general chat (owner 2026-09-29, core 2717)
  if (c.kind === 'group') return c.general ? t('chat_general') : (c.title || '')
  return ''
}
// a short two-note chime made on the spot (no audio file to load or to be blocked)
function chime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext
    if (!Ctx) return
    const ctx = chime.ctx || (chime.ctx = new Ctx())
    if (ctx.state === 'suspended') ctx.resume()
    ;[[880, 0], [1320, 0.12]].forEach(([f, at]) => {
      const o = ctx.createOscillator(), g = ctx.createGain()
      o.frequency.value = f; o.type = 'sine'
      g.gain.setValueAtTime(0.0001, ctx.currentTime + at)
      g.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + at + 0.02)
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + at + 0.25)
      o.connect(g).connect(ctx.destination); o.start(ctx.currentTime + at); o.stop(ctx.currentTime + at + 0.3)
    })
  } catch { /* sound is a nicety */ }
}
const notifyText = n => n.text || (n.photo ? `📷 ${t('chat_photo')}` : '')
const notifyTitle = n => n.kind === 'visitor_agent' ? `${n.from || t('chat_visitor')} · ${t('chat_from_site')}`
  : n.kind === 'group' ? `${n.from || t('chat_colleague')} · ${n.general ? t('chat_general') : (n.title || '')}`
  : (n.from || t('chat_colleague'))

export function ChatInboxButton({ canReadAll = false, agents = [], myAgentId = null }) {
  const [unread, setUnread] = useState({ unread: 0, visitors: 0, team: 0 })
  const [open, setOpen] = useState(null)          // null | { conv?: id, tab?: 'visitors'|'team' }
  const [toasts, setToasts] = useState([])
  const since = useRef(null)
  const shown = useRef(new Set())
  // the chat on screen right now (reported by the panel): a message in it needs no pop-up
  const viewing = useRef(null)

  const read = useCallback(() => getChatUnread().then(r => r && setUnread({ unread: r.unread || 0, visitors: r.visitors || 0, team: r.team || 0 })).catch(() => {}), [])

  const busy = useRef(false)
  const check = useCallback(async () => {
    // one check at a time, and "is this the first?" decided BEFORE the request: two overlapping first checks (React's
    // dev double-mount, or a slow network) once let the second treat the 15-minute backlog as new messages
    if (busy.current) return
    busy.current = true
    const first = since.current === null
    try {
      const r = await getChatNotify(since.current)
      if (!r) return
      // ⚠️ the next `since` (review 2026-10-27): `now` is the API server's clock but a message's time is the database's,
      // and the server answers at most 50 — so stepping to `now` skipped a message saved a moment late and everything
      // past the 50th. Step back 30 s instead (`shown` drops the repeats), or only to the last one answered when it was cut.
      const items = r.items || []
      since.current = items.length >= 50 ? items[items.length - 1].at
        : new Date(Date.parse(r.now || new Date().toISOString()) - 30000).toISOString()
      if (first) { items.forEach(n => shown.current.add(n.message_id)); return read() }   // the first call only sets the clock
      const fresh = items.filter(n => !shown.current.has(n.message_id))
      if (!fresh.length) return
      fresh.forEach(n => shown.current.add(n.message_id))
      read()
      const visible = fresh.filter(n => !(viewing.current === n.conversation_id && document.visibilityState === 'visible'))
      if (!visible.length) return
      // a muted chat (owner 2026-09-29, core 2720): "no sound but let popup show new message" — the pop-up stays,
      // the sound and the operating-system notification (which sounds too) do not
      const loud = visible.filter(n => !n.muted)
      if (loud.length) chime()
      setToasts(ts => [...ts, ...visible.map(n => ({ ...n, key: n.message_id }))].slice(-4))
      if (document.visibilityState !== 'visible' && 'Notification' in window && window.Notification.permission === 'granted') {
        for (const n of loud.slice(-3)) {
          try {
            const sys = new window.Notification(notifyTitle(n), { body: notifyText(n), tag: `chat-${n.conversation_id}`, icon: '/favicon.ico' })
            sys.onclick = () => { window.focus(); setOpen({ conv: n.conversation_id, tab: n.kind === 'visitor_agent' ? 'visitors' : 'team' }); sys.close() }
          } catch { /* some browsers only allow notifications from a service worker */ }
        }
      }
    } catch { /* the next tick tries again */ } finally { busy.current = false }
  }, [read])

  useEffect(() => {
    check()
    // keeps running in a background tab too (browsers slow it to about once a minute, which is still a notification)
    const id = setInterval(check, NOTIFY_MS)
    const onVis = () => { if (document.visibilityState === 'visible') check() }
    document.addEventListener('visibilitychange', onVis)
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVis) }
  }, [check])
  // pop-ups leave by themselves after 8 s
  useEffect(() => {
    if (!toasts.length) return
    const id = setTimeout(() => setToasts(ts => ts.slice(1)), 8000)
    return () => clearTimeout(id)
  }, [toasts])
  // other parts of the Desk may open a chat ("Discuss with the team" on a flat or a client)
  useEffect(() => {
    const on = e => { if (e.detail?.id) setOpen({ conv: e.detail.id, tab: 'team' }) }
    window.addEventListener('sentavus:open-chat', on)
    return () => window.removeEventListener('sentavus:open-chat', on)
  }, [])

  const click = () => {
    if ('Notification' in window && window.Notification.permission === 'default') window.Notification.requestPermission().catch(() => {})
    setOpen({ tab: unread.visitors === 0 && unread.team > 0 ? 'team' : 'visitors' })
  }

  return (
    <>
      <button type="button" aria-label={t('chat_title')} title={t('chat_title')}
        className="relative w-9 h-9 sm:w-8 sm:h-8 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
        onClick={click} data-testid="chat-inbox-button">
        <Icon name="message-circle" size={17} />
        {unread.unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 rounded-full bg-red-500 text-white text-[10px] leading-4 px-1 text-center tabular-nums font-semibold">{unread.unread > 99 ? '99+' : unread.unread}</span>
        )}
      </button>
      {toasts.length > 0 && createPortal(
        <div className="fixed bottom-4 right-4 z-[70] flex w-[min(360px,calc(100vw-2rem))] flex-col gap-2" data-testid="chat-toasts">
          {toasts.map(n => (
            <button key={n.key} type="button"
              onClick={() => { setToasts(ts => ts.filter(x => x.key !== n.key)); setOpen({ conv: n.conversation_id, tab: n.kind === 'visitor_agent' ? 'visitors' : 'team' }) }}
              className="flex items-start gap-3 rounded-xl border border-border bg-background p-3 text-left shadow-lg transition hover:bg-accent">
              <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${n.from_visitor ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>{initials(n.from)}</span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold">{notifyTitle(n)}</span>
                <span className="line-clamp-2 block text-[13px] text-muted-foreground">{notifyText(n)}</span>
              </span>
              <span className="shrink-0 text-[11px] text-muted-foreground">{when(n.at)}</span>
            </button>
          ))}
        </div>, document.body)}
      {open && createPortal(
        <ChatPanel canReadAll={canReadAll} agents={agents} myAgentId={myAgentId} unread={unread} start={open}
          onViewing={id => { viewing.current = id }} onClose={() => { viewing.current = null; setOpen(null); read() }} onChanged={read} />, document.body)}
    </>
  )
}

function ChatPanel({ canReadAll, agents, myAgentId, unread, start, onViewing, onClose, onChanged }) {
  const [tab, setTab] = useState(start.tab || 'visitors')
  const [scope, setScope] = useState('mine')
  const [items, setItems] = useState(null)
  const [err, setErr] = useState('')
  const [openId, setOpenId] = useState(start.conv || null)
  const [picking, setPicking] = useState(false)
  // a pop-up clicked while the panel is already open: jump to that chat
  const [seenStart, setSeenStart] = useState(start)
  if (start !== seenStart) { setSeenStart(start); if (start.conv) { setOpenId(start.conv); setTab(start.tab || 'visitors') } }

  useEffect(() => { onViewing(openId) }, [openId, onViewing])
  // only the newest request may fill the list: an answer for the tab or scope the agent just left could land last and
  // show Clients' chats under Team for up to 10 s (review 2026-10-27)
  const asked = useRef(0)
  const load = useCallback(() => {
    if (tab === 'flagged') return Promise.resolve()   // the flagged tab loads its own list
    const n = ++asked.current
    return getChatInbox(tab, scope).then(r => { if (n === asked.current) { setItems(r?.items || []); setErr('') } })
      .catch(e => { if (n === asked.current) setErr(e?.message || 'error') })
  }, [tab, scope])
  useEffect(() => {
    load()
    const id = setInterval(() => document.visibilityState === 'visible' && load(), 10000)
    return () => clearInterval(id)
  }, [load])
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const startDirect = async agentId => {
    setPicking(false)
    try { const r = await openDirectChat(agentId); setTab('team'); setOpenId(r.id); load() } catch (e) { setErr(e?.message || 'error') }
  }
  const startGroup = async (title, agentIds) => {
    try { const r = await createGroupChat(title, agentIds); setPicking(false); setTab('team'); setOpenId(r.id); load() } catch (e) { setErr(e?.message || 'error') }
  }
  // the general chat is pinned to the top of the team list; everything else keeps the server's newest-first order
  const listed = useMemo(() => (items || []).slice().sort((x, y) => (y.general ? 1 : 0) - (x.general ? 1 : 0)), [items])
  const current = items?.find(c => c.id === openId) || null
  const tabBtn = (key, label, n) => (
    <button key={key} type="button" onClick={() => { setTab(key); setOpenId(null); setPicking(false); setItems(null) }}
      data-testid={`chat-tab-${key}`}
      className={`relative shrink-0 whitespace-nowrap px-3 py-1.5 text-sm ${tab === key ? 'border-b-2 border-primary font-semibold text-foreground' : 'text-muted-foreground'}`}>
      {label}{n > 0 && <span className="ml-1.5 rounded-full bg-red-500 px-1.5 text-[10px] font-semibold text-white">{n}</span>}
    </button>
  )
  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/30" onMouseDown={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="flex h-full w-full max-w-[960px] flex-col bg-background shadow-xl sm:m-3 sm:h-[calc(100%-1.5rem)] sm:rounded-xl sm:border sm:border-border" role="dialog" aria-label={t('chat_title')}>
        {/* ⚠️ a phone (owner 2026-10-27): the title, the tabs, a manager's Mine/All and ✕ in one row are wider than the
            screen — ✕ went off it (no way to close the panel) and the last tab was cut mid-word. On a phone the tabs take
            a row of their own under the title and the controls; from sm up it is one row, as before. */}
        <div className="flex flex-wrap items-center justify-between gap-x-2 border-b border-border px-4 pt-2">
          <span className="pb-1.5 text-base font-semibold sm:hidden">{t('chat_title')}</span>
          <div className="order-last flex w-full min-w-0 items-end gap-1 overflow-x-auto sm:order-none sm:w-auto">
            <span className="mr-3 hidden shrink-0 pb-1.5 text-base font-semibold sm:inline">{t('chat_title')}</span>
            {tabBtn('visitors', t('chat_tab_clients'), unread.visitors)}
            {tabBtn('team', t('chat_tab_team'), unread.team)}
            {canReadAll && tabBtn('flagged', t('chat_tab_flagged'), 0)}
          </div>
          <div className="flex shrink-0 items-center gap-2 pb-1.5">
            {canReadAll && tab === 'visitors' && (
              <div className="flex rounded-lg border border-border text-xs">
                {['mine', 'all'].map(s => (
                  <button key={s} type="button" onClick={() => { setScope(s); setOpenId(null); setItems(null) }}
                    className={`px-2.5 py-1 ${scope === s ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'} ${s === 'mine' ? 'rounded-l-lg' : 'rounded-r-lg'}`}>
                    {t(s === 'mine' ? 'chat_scope_mine' : 'chat_scope_all')}
                  </button>
                ))}
              </div>
            )}
            <button type="button" onClick={onClose} aria-label={t('chat_close')} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-accent">✕</button>
          </div>
        </div>
        {tab === 'flagged' && <FlaggedList />}
        <div className={tab === 'flagged' ? 'hidden' : 'grid min-h-0 flex-1 grid-cols-1 md:grid-cols-[320px_minmax(0,1fr)]'}>
          <div className={`flex min-h-0 flex-col border-border md:border-r ${openId ? 'hidden md:flex' : 'flex'}`}>
            {tab === 'team' && (
              <div className="border-b border-border p-2">
                <div className="flex gap-2">
                  <button type="button" onClick={() => setPicking(p => p === 'direct' ? false : 'direct')} data-testid="chat-new-team"
                    className="flex-1 rounded-lg border border-dashed border-border px-3 py-2 text-sm text-muted-foreground hover:bg-accent">＋ {t('chat_new_team')}</button>
                  <button type="button" onClick={() => setPicking(p => p === 'group' ? false : 'group')} data-testid="chat-new-group"
                    className="flex-1 rounded-lg border border-dashed border-border px-3 py-2 text-sm text-muted-foreground hover:bg-accent">＋ {t('chat_new_group')}</button>
                </div>
                {picking === 'direct' && <AgentPicker agents={agents} myAgentId={myAgentId} onPick={startDirect} />}
                {picking === 'group' && <GroupForm agents={agents} myAgentId={myAgentId} onCreate={startGroup} />}
              </div>
            )}
            <ul className="min-h-0 flex-1 overflow-y-auto">
              {err && <li className="px-4 py-3 text-sm text-destructive">{err}</li>}
              {items && items.length === 0 && <li className="px-4 py-6 text-sm text-muted-foreground">{t(tab === 'team' ? 'chat_empty_team' : 'chat_empty')}</li>}
              {listed.map(c => (
                <li key={c.id}>
                  <button type="button" onClick={() => setOpenId(c.id)}
                    className={`flex w-full items-center gap-3 border-b border-border/60 px-4 py-3 text-left hover:bg-accent/60 ${c.id === openId ? 'bg-accent' : ''}`}>
                    <Avatar c={c} />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-baseline justify-between gap-2">
                        <span className={`truncate text-sm ${c.unread ? 'font-semibold' : 'font-medium'}`}>{titleOf(c)}{c.muted && <span className="ml-1 text-xs" title={t('chat_unmute')} aria-hidden>🔕</span>}</span>
                        <span className="shrink-0 text-[11px] text-muted-foreground">{c.at ? when(c.at) : ''}</span>
                      </span>
                      <span className="flex items-center justify-between gap-2">
                        <span className={`truncate text-[13px] ${c.unread ? 'text-foreground' : 'text-muted-foreground'}`}>{c.preview || ''}</span>
                        {c.unread > 0 && <span className="min-w-[18px] shrink-0 rounded-full bg-primary px-1.5 text-center text-[11px] font-semibold text-primary-foreground">{c.unread}</span>}
                      </span>
                      {c.kind === 'visitor_agent' && (!c.mine || c.waiting_since) && (
                        <span className="mt-0.5 block text-[11px] text-muted-foreground">
                          {!c.mine && c.agent?.name ? `→ ${c.agent.name}` : ''}{!c.mine && c.waiting_since ? ' · ' : ''}{c.waiting_since ? t('chat_waiting') : ''}
                        </span>
                      )}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <section className={`min-h-0 flex-col ${openId ? 'flex' : 'hidden md:flex'}`}>
            {openId
              ? <Chat key={openId} id={openId} hint={current} agents={agents} myAgentId={myAgentId} onBack={() => setOpenId(null)} onChanged={() => { load(); onChanged() }} />
              : <div className="flex flex-1 items-center justify-center p-8 text-sm text-muted-foreground">{t('chat_pick')}</div>}
          </section>
        </div>
      </div>
    </div>
  )
}

// Management only (the tab renders for canReadAll; the server refuses anyone else): agents' team messages that
// carried a phone number — the owner's ruling was "allow it, flag it for managers". Each row says who sent it, to
// whom, in which kind of chat and about what, so a manager can judge it without opening a private direct chat.
function FlaggedList() {
  const [items, setItems] = useState(null)
  const [err, setErr] = useState('')
  useEffect(() => {
    let live = true
    getChatFlagged().then(r => { if (live) setItems(r?.items || []) }).catch(e => { if (live) setErr(e?.message || 'error') })
    return () => { live = false }
  }, [])
  const where = f => f.kind === 'direct' ? t('chat_flagged_direct') : f.kind === 'property' ? titleOf({ kind: 'property', subject: f.subject })
    : f.kind === 'client' ? titleOf({ kind: 'client', subject: f.subject }) : ''
  return (
    <div className="min-h-0 flex-1 overflow-y-auto" data-testid="chat-flagged">
      <p className="border-b border-border px-4 py-2.5 text-xs text-muted-foreground">{t('chat_flagged_intro')}</p>
      {err && <p className="px-4 py-3 text-sm text-destructive">{err}</p>}
      {items && items.length === 0 && <p className="px-4 py-6 text-sm text-muted-foreground">{t('chat_flagged_empty')}</p>}
      <ul>
        {(items || []).map(f => (
          <li key={f.message_id} className="border-b border-border/60 px-4 py-3">
            <div className="flex items-baseline justify-between gap-2 text-sm">
              <span className="min-w-0 truncate">
                <span className="font-semibold">{f.from || t('chat_colleague')}</span>
                {f.to?.length > 0 && <span className="text-muted-foreground"> → {f.to.join(', ')}</span>}
              </span>
              <span className="shrink-0 text-[11px] text-muted-foreground">{when(f.at)}</span>
            </div>
            {where(f) && <div className="text-[11px] text-muted-foreground">{where(f)}</div>}
            <p className="mt-1 whitespace-pre-wrap break-words text-sm"><span className="mr-1 text-amber-600" aria-hidden>⚑</span>{f.text}</p>
          </li>
        ))}
      </ul>
    </div>
  )
}

function Avatar({ c, size = 'h-10 w-10' }) {
  const icon = c?.kind === 'property' ? 'home' : c?.kind === 'client' ? 'user' : c?.kind === 'group' ? 'users' : null
  return (
    <span className={`flex ${size} shrink-0 items-center justify-center rounded-full text-sm font-semibold ${c?.kind === 'visitor_agent' ? 'bg-primary/15 text-primary' : 'bg-muted'}`}>
      {icon ? <Icon name={icon} size={16} /> : initials(titleOf(c))}
    </span>
  )
}

// a new group: a name and the colleagues in it (the creator is always a member)
function GroupForm({ agents, myAgentId, onCreate, exclude = [], addOnly = false }) {
  const [title, setTitle] = useState('')
  const [picked, setPicked] = useState([])
  const [busy, setBusy] = useState(false)
  const names = useMemo(() => new Map((agents || []).map(a => [a.id, a.name])), [agents])
  const ready = picked.length > 0 && (addOnly || title.trim())
  const submit = async () => {
    if (!ready || busy) return
    setBusy(true)
    try { await onCreate(title.trim(), picked) } finally { setBusy(false) }
  }
  return (
    <div className="mt-2 space-y-2" data-testid="chat-group-form">
      {!addOnly && <input autoFocus value={title} onChange={e => setTitle(e.target.value)} maxLength={80} placeholder={t('chat_group_name')}
        className="w-full rounded-lg border border-border bg-background px-3 py-2 text-base sm:text-sm focus:outline-none" />}
      {picked.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {picked.map(id => (
            <button key={id} type="button" onClick={() => setPicked(p => p.filter(x => x !== id))}
              className="rounded-full bg-accent px-2 py-0.5 text-xs">{names.get(id) || '?'} ✕</button>
          ))}
        </div>
      )}
      <AgentPicker agents={(agents || []).filter(a => !picked.includes(a.id) && !exclude.includes(a.name))} myAgentId={myAgentId}
        placeholder={t('chat_group_pick')} onPick={id => setPicked(p => (p.includes(id) ? p : [...p, id]))} />
      <button type="button" onClick={submit} disabled={!ready || busy} data-testid="chat-group-create"
        className="w-full rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50">
        {t(addOnly ? 'chat_group_added' : 'chat_group_create')}
      </button>
    </div>
  )
}

function AgentPicker({ agents, myAgentId, onPick, placeholder }) {
  const [q, setQ] = useState('')
  const list = useMemo(() => {
    const s = q.trim().toLowerCase()
    return (agents || []).filter(a => a.is_active !== false && a.id !== myAgentId && (!s || String(a.name || '').toLowerCase().includes(s)))
      .sort((x, y) => String(x.name || '').localeCompare(String(y.name || ''))).slice(0, 30)
  }, [agents, myAgentId, q])
  return (
    <div className="mt-2 rounded-lg border border-border">
      <input autoFocus={!placeholder} value={q} onChange={e => setQ(e.target.value)} placeholder={placeholder || t('chat_find_colleague')}
        className="w-full rounded-t-lg border-b border-border bg-background px-3 py-2 text-base sm:text-sm focus:outline-none" />
      <ul className="max-h-60 overflow-y-auto">
        {list.map(a => (
          <li key={a.id}>
            <button type="button" onClick={() => onPick(a.id)} className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-muted text-[11px] font-semibold">{initials(a.name)}</span>{a.name}
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}

function Chat({ id, hint, onBack, onChanged, agents = [], myAgentId = null }) {
  const [thread, setThread] = useState(null)
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState('')
  const [sending, setSending] = useState(false)
  const [err, setErr] = useState('')
  const lastId = useRef(0)
  const lastTyping = useRef(0)
  const bottom = useRef(null)
  const fileInput = useRef(null)
  // the parent hands a fresh callback every render; reading it through a ref keeps `poll` stable
  const changed = useRef(onChanged)
  useEffect(() => { changed.current = onChanged }, [onChanged])

  const poll = useCallback((first = false) => getChatThread(id, lastId.current).then(r => {
    const fresh = r?.messages || []
    if (fresh.length) lastId.current = fresh[fresh.length - 1].id
    // merge by id: two overlapping polls (a slow network) must never show a message twice
    setThread(prev => (prev ? { ...r, messages: [...prev.messages, ...fresh.filter(m => !prev.messages.some(p => p.id === m.id))] } : r))
    if (first || fresh.some(m => m.from === 'them')) markChatSeen(id).then(() => changed.current()).catch(() => {})
  }).catch(e => { if (first) setErr(e?.message || 'error') }), [id])

  useEffect(() => {
    poll(true)
    const iv = setInterval(() => document.visibilityState === 'visible' && poll(), 3000)
    // back from another tab: catch up at once instead of on the next tick
    const onVis = () => { if (document.visibilityState === 'visible') poll() }
    document.addEventListener('visibilitychange', onVis)
    return () => { clearInterval(iv); document.removeEventListener('visibilitychange', onVis) }
  }, [poll])
  const msgs = useMemo(() => thread?.messages || [], [thread])
  useEffect(() => { bottom.current?.scrollIntoView({ block: 'end' }) }, [msgs.length])

  async function send(attachment = null) {
    const text = attachment ? '' : draft.trim()
    if ((!text && !attachment) || sending) return
    setSending(true); setErr('')
    try { await postChatMessage(id, text, attachment); if (!attachment) setDraft(''); await poll(); changed.current() }
    catch { setErr(t('chat_send_failed')) }
    finally { setSending(false) }
  }
  async function sendPhoto(file) {
    if (!file) return
    if (!/^image\/(jpeg|png|webp)$/.test(file.type) || file.size > 5 * 1024 * 1024) { setErr(t('chat_photo_invalid')); return }
    setSending(true); setErr('')
    let att = null
    try { att = await uploadChatPhoto(id, file) } catch { setErr(t('chat_send_failed')) }
    setSending(false)
    if (att) await send(att)
  }
  function typing(v) {
    setDraft(v)
    if (v && Date.now() - lastTyping.current > 3000) { lastTyping.current = Date.now(); sendChatTyping(id).catch(() => {}) }
  }

  const c = thread || hint || {}
  const canWrite = thread ? thread.can_write : false
  const lastMine = [...msgs].reverse().find(m => m.from === 'me')
  // as times, not text: the server writes `…Z` and the database `…+00:00`, which do not compare as strings
  const seen = !!(lastMine && thread?.seen_by_them_at && Date.parse(thread.seen_by_them_at) >= Date.parse(lastMine.at))
  const team = c.kind && c.kind !== 'visitor_agent'

  return (
    <>
      <div className="flex items-center gap-3 border-b border-border px-3 py-2.5">
        <button type="button" onClick={onBack} className="w-9 h-9 flex items-center justify-center text-2xl leading-none md:hidden" aria-label={t('chat_back')}>‹</button>
        <Avatar c={c} size="h-9 w-9" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold">{titleOf(c)}</div>
          <div className="flex flex-wrap gap-x-3 text-xs text-muted-foreground">
            {thread?.typing?.length > 0
              ? <span className="text-primary">{thread.typing.join(', ')} {t('chat_typing')}</span>
              : c.kind === 'group'
                ? <span className="truncate" data-testid="chat-group-members">{c.general ? t('chat_general_hint') : (thread?.members || []).join(', ')}</span>
                : <>
                  {c.visitor?.email && <a href={`mailto:${c.visitor.email}`} className="underline">{c.visitor.email}</a>}
                  {c.pid && <a href={`${SITE}/ka/property/${c.pid}`} target="_blank" rel="noopener noreferrer" className="underline">{t('chat_property')} ↗</a>}
                </>}
          </div>
        </div>
        {thread && c.kind && c.kind !== 'visitor_agent' && (
          <button type="button" data-testid="chat-mute"
            onClick={async () => {
              const next = !thread.muted
              setThread(th => (th ? { ...th, muted: next } : th))
              try { await muteChat(id, next); changed.current() } catch { setThread(th => (th ? { ...th, muted: !next } : th)); setErr(t('chat_send_failed')) }
            }}
            title={t(thread.muted ? 'chat_unmute' : 'chat_mute')} aria-label={t(thread.muted ? 'chat_unmute' : 'chat_mute')} aria-pressed={!!thread.muted}
            className={`relative flex h-9 w-9 shrink-0 items-center justify-center rounded-lg hover:bg-accent ${thread.muted ? 'text-muted-foreground/60' : 'text-muted-foreground'}`}>
            <Icon name="bell" size={16} />
            {thread.muted && <span aria-hidden className="absolute h-[2px] w-5 rotate-45 rounded bg-current" />}
          </button>
        )}
        {c.kind === 'group' && !c.general && thread?.can_write && (
          <button type="button" onClick={() => setAdding(v => !v)} title={t('chat_group_add')} aria-label={t('chat_group_add')} data-testid="chat-group-add"
            className="flex h-9 shrink-0 items-center gap-1 rounded-lg px-2 text-xs text-muted-foreground hover:bg-accent">
            <Icon name="users" size={15} />＋
          </button>
        )}
      </div>
      {adding && (
        <div className="border-b border-border p-2">
          <GroupForm addOnly agents={agents} myAgentId={myAgentId} exclude={thread?.members || []} onCreate={async (_, ids) => {
            try { const r = await addChatMembers(id, ids); setThread(th => (th ? { ...th, members: r?.members || th.members } : th)); setAdding(false) }
            catch { setErr(t('chat_send_failed')) }
          }} />
        </div>
      )}
      <div className="min-h-0 flex-1 space-y-1.5 overflow-y-auto bg-muted/30 px-3 py-4">
        {err && <p className="text-sm text-destructive">{err}</p>}
        {msgs.map(m => (
          <div key={m.id} className={`flex ${m.from === 'me' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] whitespace-pre-wrap break-words px-3.5 py-2 text-sm leading-snug ${m.from === 'me' ? 'rounded-2xl rounded-br-md bg-primary text-primary-foreground' : 'rounded-2xl rounded-bl-md bg-background border border-border'}`}>
              {team && c.kind !== 'direct' && m.from !== 'me' && m.sender?.name && <span className="mb-0.5 block text-[11px] font-semibold text-primary">{m.sender.name}</span>}
              {m.photo && <a href={m.photo} target="_blank" rel="noopener noreferrer"><img src={m.photo} alt="" className="mb-1 max-h-64 rounded-lg" /></a>}
              {m.text}
              {m.flagged && <span className="ml-2 text-[10px] text-amber-600" title={t('chat_flagged_hint')}>⚑</span>}
              <span className={`ml-2 inline-block align-bottom text-[10px] ${m.from === 'me' ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>{when(m.at)}</span>
            </div>
          </div>
        ))}
        {seen && <div className="text-right text-[11px] text-muted-foreground">{t('chat_seen')}</div>}
        <div ref={bottom} />
      </div>
      {!thread ? null : canWrite ? (
        <form onSubmit={e => { e.preventDefault(); send() }} className="flex items-end gap-2 border-t border-border p-2.5">
          <input ref={fileInput} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
            onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; sendPhoto(f) }} />
          <button type="button" onClick={() => fileInput.current?.click()} disabled={sending} aria-label={t('chat_photo')} title={t('chat_photo')}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-accent disabled:opacity-50">
            <Icon name="camera" size={18} />
          </button>
          <textarea value={draft} onChange={e => typing(e.target.value)} rows={1} maxLength={4000}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); send() } }}
            placeholder={t('chat_placeholder')} data-testid="chat-input"
            className="min-h-10 max-h-40 flex-1 resize-none rounded-2xl border border-input bg-background px-3.5 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
          <button type="submit" disabled={!draft.trim() || sending}
            className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50">{t('chat_send')}</button>
        </form>
      ) : (
        <div className="border-t border-border px-4 py-3 text-xs text-muted-foreground">{t('chat_read_only')}</div>
      )}
    </>
  )
}
